import { styled } from "goober";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { playClick } from "../lib/sounds";
import { userDisplayName } from "../lib/userDisplayName";
import { ConnectorLogo } from "./ConnectorLogo";
import { Row } from "./ConnectorDropdown";
import { Text } from "./Text";

export type CreatedBy =
  | { name: string; email: string }
  | readonly { name: string; email: string }[]
  | null;

export type AppInfo = ConnectorWithOAuth & { createdBy?: CreatedBy };

export function createdByName(createdBy: CreatedBy | undefined): string | null {
  if (!createdBy) return null;
  if (Array.isArray(createdBy)) {
    const first = createdBy[0];
    return first ? userDisplayName(first) : null;
  }
  return userDisplayName(createdBy as { name: string; email: string });
}

const LeftIcon = styled("div")`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

type Props = {
  name: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
  size?: number;
  grayscale?: boolean;
} & (
  | { app: ConnectorWithOAuth; template?: never }
  | { app?: never; template: { name: string; identifier: string } }
);

export function ConnectorRow({
  name,
  secondary,
  trailing,
  size = 16,
  grayscale,
  ...logo
}: Props) {
  return (
    <>
      <LeftIcon
        style={
          grayscale ? { filter: "grayscale(100%)", opacity: 0.4 } : undefined
        }
      >
        {logo.app ? (
          <ConnectorLogo app={logo.app} size={size} />
        ) : logo.template ? (
          <ConnectorLogo template={logo.template} size={size} />
        ) : null}
      </LeftIcon>
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {name}
        </Text>
        {secondary && (
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            {secondary}
          </Text>
        )}
      </div>
      {trailing}
    </>
  );
}

// A clickable connector row: navigates to `to`, or runs `onClick`, then closes
// the dropdown. `idle`/`hover` are trailing slots (idle shows at rest, hover on
// row hover) for affordances like a checkmark, open link, or remove button.
export function ConnectorOption({
  app,
  onClick,
  to,
  idle,
  hover,
  close,
}: {
  app: AppInfo;
  onClick?: () => void;
  to?: string;
  idle?: React.ReactNode;
  hover?: React.ReactNode;
  close: () => void;
}) {
  return (
    <Row
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        playClick();
        close();
        if (to) history.pushState(history.state, "", to);
        else onClick?.();
      }}
    >
      <ConnectorRow
        app={app}
        name={app.name}
        secondary={createdByName(app.createdBy)}
        trailing={
          (idle || hover) && (
            <>
              {idle && <span className="row-idle">{idle}</span>}
              {hover && (
                <div
                  className="row-hover"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {hover}
                </div>
              )}
            </>
          )
        }
      />
    </Row>
  );
}
